import React, { useCallback } from "react";
import { Query } from "react-apollo";
import * as R from "ramda";
import { css } from "glamor";
import matchSorter from "match-sorter";
import sanitizeHtml from "sanitize-html";
import { Input } from "antd";

import Table from "@arachnys/styleguide/Table";
import CollapsibleTextViewer from "@arachnys/flow-app/src/component/CollapsibleTextViewer";
import CollapsibleLinkViewer from "@arachnys/flow-app/src/component/CollapsibleLinkViewer";
import { generateEntityData } from "@arachnys/flow-app/src/container/EntityViewer";
import GenericViewer from "@arachnys/flow-app/src/component/GenericViewer";
import { withApolloState } from "@arachnys/flow-app/src/util/apollo";
import { formatDatetimeString } from "@arachnys/flow-app/src/formatters";
import { removeTypenames } from "@arachnys/flow-app/src/util";
import DispositionOverlayBar from "./DispositionOverlayBar";
import Comments from "./Comments";
import { expanderIconRow } from "./ExpanderIcon";
import { GET_ENTITY_DETAILS } from "./queries";

const styles = {
    snippet: css({
        " em": {
            fontStyle: "normal",
            fontWeight: "bold",
            backgroundColor: "#ff8",
        },
    }),
    resultDetails: css({
        padding: "0 20px 10px",
        backgroundColor: "#e6f6fd",
    }),
    resultDetailsTable: css({
        width: "100%",
    }),
    resultDetailsList: css({
        listStyleType: "none",
        paddingLeft: 0,
    }),
    dispositionHeader: {
        backgroundColor: "#ccc",
    },
    genericViewerItemContainer: css({
        display: "flex",
        flexWrap: "nowrap",
    }),
    genericViewerTitle: css({
        display: "block",
        minWidth: 150,
        textAlign: "right",
        fontWeight: "bold",
        marginRight: 20,
    }),
    genericViewerValue: css({
        display: "block",
        flexGrow: "1",
        wordBreak: "break-word",
    }),
    inputFilter: css({
        fontSize: 13,
        height: 28,
    }),
};

const getTagLabels = R.pluck("label");
const transformLabelsToDisplay = R.join(" / ");
const getTagsDisplayForAttribute = R.curry(
    R.pipe(R.prop, getTagLabels, transformLabelsToDisplay)
);
const getDispositionDisplay = getTagsDisplayForAttribute("tags");
const getReviewDisplay = getTagsDisplayForAttribute("reviewTags");
const orderData = data => {
    const orderedData = {};
    Object.keys(data)
        .sort()
        .forEach(key => {
            orderedData[key] = data[key];
        });
    return orderedData;
};

const filterMethod = R.curry((keys, filter, rows) => {
    const traversers = R.map(
        key => obj => {
            const isRelationTitle = R.pathOr(
                false,
                ["_original", "isRelationTitle"],
                obj
            );
            if (!isRelationTitle) {
                return obj[key];
            }
            // Ignore relation titles; otherwise they just bunch up together
            // and don't act as titles.
            return undefined;
        },
        keys
    );
    return matchSorter(rows, filter.value, { keys: traversers });
});

const FilterTitleField = ({
    placeholder,
    filter,
    id,
    onClick = () => {},
    title,
    showFilter,
    appliedFilters = [],
    className,
    inputClassName,
}) => {
    const value = R.pathOr(
        "",
        ["value"],
        R.find(R.propEq("id", id))(appliedFilters)
    );

    const handleChange = useCallback(
        e => filter({ id, value: e.target.value }),
        [id, filter]
    );

    const handleClick = useCallback(
        e => {
            e.stopPropagation();
            onClick(e);
        },
        [onClick]
    );

    return (
        <div className={className}>
            {showFilter ? (
                <Input
                    value={value}
                    placeholder={placeholder}
                    onChange={handleChange}
                    onClick={handleClick}
                    className={`${css(styles.inputFilter, inputClassName)}`}
                />
            ) : (
                title
            )}
        </div>
    );
};

const withEntityDetails = (Component, id, props) => (
    <Query
        query={GET_ENTITY_DETAILS}
        fetchPolicy="cache-and-network"
        variables={{ id }}
    >
        {withApolloState(({ data }) => {
            const entityData = generateEntityData(removeTypenames(data.entity));
            return (
                <Component
                    data={entityData}
                    schema={data.EntitySchema}
                    {...props}
                />
            );
        })}
    </Query>
);

const viewerProps = {
    ignoreEmpty: true,
    styles: {
        title: styles.genericViewerTitle,
    },
    className: {
        object: styles.resultDetailsTable,
        array: styles.resultDetailsList,
        infoItem: styles.genericViewerItemContainer,
        infoValue: styles.genericViewerValue,
    },
};

const renderEntityDetails = (row, dispositionBarConfig, readOnly) => {
    const { comments, original, title, id, isRelationTitle } = row.original;
    const ulid = R.path(["original", "entityKey", "ulid"], row);
    const snippet = row.original.snippet.substring(0, 249);

    let data = {
        title: (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(title),
                }}
            />
        ),
    };
    let viewer;

    if (original && !isRelationTitle) {
        const { names, ...otherData } = original;
        if (otherData["Hit Details"])
            otherData["Hit Details"] = (
                <CollapsibleTextViewer
                    data={otherData["Hit Details"]}
                    maxLength={250}
                />
            );
        if (otherData["Source Web Link"].length)
            otherData["Source Web Link"] = (
                <CollapsibleLinkViewer
                    data={otherData["Source Web Link"]}
                    maxLength={5}
                />
            );
        delete otherData["Matching Score"];
        const orderedData = orderData(otherData);
        data = {
            ...data,
            names: R.map(
                nameObj =>
                    Object.entries(nameObj).map(([key, name]) => (
                        <div
                            key={key}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(name),
                            }}
                        />
                    )),
                names
            ),
            ...orderedData,
        };
    }

    if (!isRelationTitle && snippet && snippet.length) {
        data = {
            ...data,
            snippet: (
                <div
                    {...styles.snippet}
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(snippet),
                    }}
                />
            ),
        };
    }

    if (isRelationTitle) {
        const entityId = btoa(`Entity:${ulid}`);
        viewer = withEntityDetails(GenericViewer, entityId, viewerProps);
    } else {
        viewer = <GenericViewer {...viewerProps} data={data} />;
    }

    return (
        <div
            data-testid="disposition-table-subcomponent"
            className={styles.resultDetails}
        >
            {viewer}
            {!isRelationTitle && (
                <>
                    <Comments
                        editComment={
                            dispositionBarConfig.editComment &&
                            dispositionBarConfig.editComment(id)
                        }
                        deleteComment={
                            dispositionBarConfig.deleteComment &&
                            dispositionBarConfig.deleteComment(id)
                        }
                        readOnly={readOnly}
                        comments={comments}
                    />
                    {readOnly ? null : (
                        <DispositionOverlayBar
                            {...dispositionBarConfig}
                            staticPosition
                            applyDispositions={dispositionBarConfig.applyDispositions(
                                row.original.id
                            )}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const dispositionColumns = (
    resultType,
    appliedFilters,
    onFiltered = () => {},
    titleFilter = false
) => {
    const dispositionColumns = [
        {
            id: "dispositions",
            className: "dispositions",
            Header: (
                <FilterTitleField
                    filter={onFiltered}
                    placeholder="Disposition"
                    title="Disposition"
                    id="dispositions"
                    showFilter={titleFilter}
                    appliedFilters={appliedFilters}
                />
            ),
            getHeaderProps: () => ({
                style: styles.dispositionHeader,
            }),
            accessor: getDispositionDisplay,
            minWidth: 110,
            filterMethod: filterMethod(["dispositions"]),
            filterAll: true,
        },
        {
            id: "review_dispositions",
            className: "review_dispositions",
            getHeaderProps: () => ({
                style: styles.dispositionHeader,
            }),
            Header: (
                <FilterTitleField
                    filter={onFiltered}
                    placeholder="Review"
                    title="Review"
                    id="review_dispositions"
                    showFilter={titleFilter}
                    appliedFilters={appliedFilters}
                />
            ),
            accessor: getReviewDisplay,
            width: 110,
            filterMethod: filterMethod(["review_dispositions"]),
            filterAll: true,
        },
    ];

    switch (resultType) {
        case "world_compliance": {
            return [
                expanderIconRow,
                {
                    id: "title",
                    className: "title",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Entity Name"
                            title="Entity Name"
                            id="title"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: "title",
                    minWidth: 150,
                    Cell: row => {
                        const innerHTMLProp = {
                            __html: sanitizeHtml(row.value),
                        };
                        return <div dangerouslySetInnerHTML={innerHTMLProp} />;
                    },
                    filterMethod: filterMethod(["title"]),
                    filterAll: true,
                },
                {
                    id: "type",
                    className: "entry-type",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Type"
                            title="Type"
                            id="type"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: r => r.properties && r.properties.type,
                    width: 100,
                    filterMethod: filterMethod(["type"]),
                    filterAll: true,
                },
                {
                    id: "category",
                    className: "category",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Category"
                            title="Category"
                            id="category"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: r => r.properties && r.properties.category,
                    filterMethod: filterMethod(["category"]),
                    width: 100,
                    filterAll: true,
                },
                {
                    id: "subcategory",
                    className: "subcategory",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Subcategory"
                            title="Subcategory"
                            id="subcategory"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: r => r.original && r.original["Hit Subcategory"],
                    filterMethod: filterMethod(["subcategory"]),
                    width: 120,
                    filterAll: true,
                },
                {
                    id: "country",
                    className: "country",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Country"
                            title="Country"
                            id="country"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: r => r.properties && r.properties.country,
                    width: 150,
                    filterMethod: filterMethod(["country"]),
                    filterAll: true,
                },
                ...dispositionColumns,
            ];
        }
        default: {
            return [
                expanderIconRow,
                {
                    id: "published",
                    className: "published",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Published"
                            title="Published"
                            id="published"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: "publishedDate",
                    width: 100,
                    Cell: row => formatDatetimeString({ str: row.value }),
                    filterMethod: filterMethod(["published"]),
                    filterAll: true,
                },
                {
                    id: "title",
                    className: "title",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Title"
                            title="Title"
                            id="title"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: "title",
                    minWidth: 200,
                    Cell: row => {
                        const url = R.pathOr("", ["original", "url"], row);

                        // If there is no URL, don't use <a>
                        if (url.length) {
                            return (
                                <a
                                    href={url}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    {row.value}
                                </a>
                            );
                        } else {
                            return <>{row.value}</>;
                        }
                    },
                    filterMethod: filterMethod(["title"]),
                    filterAll: true,
                },
                {
                    id: "snippet",
                    className: "snippet",
                    Header: (
                        <FilterTitleField
                            filter={onFiltered}
                            placeholder="Snippet"
                            title="Snippet"
                            id="snippet"
                            showFilter={titleFilter}
                            appliedFilters={appliedFilters}
                        />
                    ),
                    accessor: "snippet",
                    minWidth: 200,
                    Cell: row => {
                        const innerHTMLProp = {
                            __html: sanitizeHtml(row.value),
                        };
                        return <div dangerouslySetInnerHTML={innerHTMLProp} />;
                    },
                    filterMethod: filterMethod(["snippet"]),
                    filterAll: true,
                },
                ...dispositionColumns,
            ];
        }
    }
};

const DispositionTable = ({ data, ...props }) => (
    <Table
        SubComponent={row =>
            renderEntityDetails(row, props.dispositionBarConfig, props.readOnly)
        }
        columns={dispositionColumns(
            props.resultType,
            props.filtered,
            props.onFiltered,
            props.titleFilterable
        )}
        data={data}
        {...props}
    />
);

export default DispositionTable;
